"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var mapActions_1 = require("./mapActions");
var resolvers_1 = require("./resolvers");
/**
 * Creates react context with state store. Store consists of state properties and actions which modify it.
 * @param initialState initial state
 *
 * @param actions is a function which creates dictionary (key-value object) of action creators for a given store. Actions might have arguments, can modify state and return values.
 * Actions creator provide special [[ContextReference]] object for all action-creators, which has:
 * * `setState` method to modify state
 * * `state` object to access current state of type
 * * `stores` object to access injected store contexts
 *
 * Any argument in action creator will be converted to action parameter, which user must provide when calling action.
 *
 * Example:
 * * Action creator:
 * ```typescript
 * (reference) => {
 *      actionName: (payload: string) => {
 *           reference.setState({whatever: payload})
 *      }
 * }
 * ```
 * * Action:
 * ```typescript
 * store.actionName("Hello world")
 * ```
 *
 * @param options is special configuration object with 2 candidate properties: `contexts` and `middleware`
 * * `contexts` is a dictionary (key-value object) of injected store contexts. Those stores will be resolved and passed to actions in `stores` property of [[ContextReference]]
 * * `middleware` is an array of used middleware. Middleware is special function which is executed after store action is called and before it is actually exectude.
 *      Examples of middleware could be LogginMiddleware, which logs every action request to console, or ErrorHandlerMiddleware, which wraps every request in `try...catch` block
 *
 * Example:
 * ```typescript
 * createStoreContext({foo: '123'}, (reference) => ({
 *  action: () => {
 *      reference.stores.bar.someBarAction()
 *      return reference.stores.bar.someBarProperty
 *  }
 * }), {
 *      contexts: {  bar: barContext },
 *      middleware: [ loggingMiddleware, errorHandlerMiddleware ]
 * })
 * ```
 *
 * @typeparam TState any JS object representing application or application part state
 * @typeparam TActions creator function of a dictionary (key-value object) of action creators
 * @typeparam TContexts a dictionary (key-value object) of injected store contexts
 *
 * @returns [context, Provider] tuple, where context is [[React.Context]] and should be consumed to access store,
 * and Provider is [[React.FC]] (functional component) without props, which enables store actions for any nested components
 */
function createStoreContext(initialState, actions, options) {
    if (actions === void 0) { actions = {}; }
    if (options === void 0) { options = {}; }
    var _meta = options.meta || {};
    var store = __assign({}, initialState, mapActions_1.mapActionsToDefault(initialState, _meta, actions));
    var context = React.createContext(store);
    var provider = function (props) {
        var _a = React.useState(initialState), _state = _a[0], setState = _a[1];
        var contexts = options.contexts, middleware = options.middleware;
        var _stores = resolvers_1.resolveStores(contexts);
        var _middleware = resolvers_1.resolveMiddleware(middleware);
        var _actions = mapActions_1.mapActionsToDispatch({
            state: _state,
            setState: setState,
            stores: _stores,
            meta: _meta
        }, actions, _middleware);
        var _store = __assign({}, _state, _actions);
        return (React.createElement(context.Provider, { value: _store }, props.children));
    };
    return [context, provider];
}
exports.default = createStoreContext;



//////////////////
// WEBPACK FOOTER
// ./node_modules/react-concise-state/dist/createStoreContext.js
// module id = 423
// module chunks = 0